<template>
  <b-card class="filter">
    <h3 class="pb-4">Filter activity logs</h3>

    <div class="row">
      <div class="col-3">
        <SimpleDropdown v-model="entityType" :options="entityTypeOptions" />
      </div>
      <div class="col-3">
        <SimpleDropdown v-model="userId" :options="userOptions" />
      </div>
      <div class="col-3 date-block">
        <VueDatePicker
          v-model="dateRange"
          format="yyyy-MM-dd"
          range
          multi-calendars
          placeholder="Created at"
          :clearable="true"
        />
      </div>
      <div class="col-3 text-right">
        <b-button variant="success" class="submit-button" @click="submit">Submit</b-button>
        <b-button v-if="hasFilters" class="ml-2" variant="secondary" @click="reset">Reset</b-button>
      </div>
    </div>
  </b-card>
</template>

<script lang="ts">
import type { SelectOptions } from '@/types/Components/SelectOptions'
import type { StaffUser } from '@/types/Models/StaffUser'
import type { ComputedRef, Ref } from 'vue'
import { VueDatePicker } from '@vuepic/vue-datepicker'
import { BButton, BCard } from 'bootstrap-vue-next'
import { ref, computed, onMounted, watch, defineComponent } from 'vue'
import { none } from '@/services/Helpers'
import { useAuditLogStore } from '@/store/auditLog'
import moment from 'moment'
import AccountProxy from '@/proxies/AccountProxy'
import SimpleDropdown from '@/views/Components/PageParts/SimpleDropdown.vue'

export default defineComponent({
  name: 'AuditLogSearchForm',
  components: { BButton, BCard, VueDatePicker, SimpleDropdown },
  setup() {
    const auditLogStore = useAuditLogStore()

    const entityType: Ref<string | null> = ref(null)
    const userId: Ref<string | null> = ref(null)
    const dateRange: Ref<string[] | null> = ref(null)
    const userOptions: Ref<SelectOptions[]> = ref([{ value: null, text: 'All users' }])
    const entityTypeOptions: SelectOptions[] = [
      { value: null, text: 'All types' },
      { value: 'company', text: 'Company' },
      { value: 'user', text: 'User' },
      { value: 'api_key', text: 'API Key' },
      { value: 'tax_number', text: 'Tax Number' },
      { value: 'priority', text: 'Priority' },
      { value: 'price', text: 'Price' },
      { value: 'company_address', text: 'Company Address' },
      { value: 'price_weight', text: 'Price Weight' },
      { value: 'address_book', text: 'Address Book' },
      { value: 'report_subscription', text: 'Report Subscription' },
    ]

    const hasFilters: ComputedRef<boolean> = computed(
      () => Boolean(entityType.value) || Boolean(userId.value) || Boolean(dateRange.value),
    )

    const submit = () => {
      const filter: Record<string, string | null> = {
        entityType: entityType.value || null,
        userId: userId.value || null,
        dateFrom: null,
        dateTo: null,
      }

      if (Array.isArray(dateRange.value) && dateRange.value.length === 2) {
        const [dateFrom, dateTo] = dateRange.value
        const parsedFrom = dateFrom ? moment(dateFrom) : null
        const parsedTo = dateTo ? moment(dateTo) : null

        if (parsedFrom?.isValid() && parsedTo?.isValid()) {
          filter.dateFrom = parsedFrom.format('YYYY-MM-DD')
          filter.dateTo = parsedTo.format('YYYY-MM-DD')
        }
      }

      auditLogStore.updateFilter(filter)
    }

    const reset = () => {
      entityType.value = null
      userId.value = null
      dateRange.value = null
      auditLogStore.updateFilter({
        entityType: null,
        userId: null,
        dateFrom: null,
        dateTo: null,
      })
    }

    const loadUsers = () => {
      new AccountProxy({ type: 'staff', limit: 200, page: 1 })
        .all()
        .then((response: { data: StaffUser[] }) => {
          const options: SelectOptions[] = [{ value: null, text: 'All users' }]
          response.data.forEach((user: StaffUser) => {
            const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
            options.push({
              value: user.id,
              text: name ? `${name} (${user.email})` : user.email,
            })
          })
          userOptions.value = options
        })
        .catch(none)
    }

    watch(dateRange, submit)

    onMounted(loadUsers)

    return {
      entityType,
      userId,
      dateRange,
      userOptions,
      entityTypeOptions,
      hasFilters,
      submit,
      reset,
    }
  },
})
</script>

